import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { Divider, Heading, Modal, Toast, useToast } from '@zillow/constellation-10';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { useIsFetching, useIsMutating, useQueryClient } from '@zg-rentals/react-query5';
import { isSigningCompleteModalOpenState } from '../../../../atoms';

import ZDocsDocumentView from '../../../components/ZDocsDocumentView';
import LeaseActionButtonGroup from '../../../components/LeaseActions/LeaseActionButtonGroup';
import { useLeasingUrlParams } from '../../../hooks/useLeasingUrlParams';
import {
  useLeaseSummaryFromOuidQuery,
  useLeaseSummaryQuery,
  useMarkAsSignedFromOuidMutation,
  useMarkAsSignedMutation,
  usePreviewUrlFromOuidQuery,
  usePreviewUrlQuery,
  useSubmitDocumentChangesMutation,
  useWebAnalyticsForListingQuery,
} from '../../../utils/queries';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import { Person } from '../../../models';
import type { Lease, Property } from '../../../models';
import { DocumentMode } from '../../../models/Types';
import { RENTER_HUB_ROOT } from '../../../constants/routeConstants';
import TenantSigningCompleteModal from '../ListView/LeaseSignPreview/TenantSigningCompleteModal';
import CountersigningCompleteModal from '../ListView/LeaseSignPreview/CountersigningCompleteModal';
import DeclineSigningModal from '../ListView/LeaseSignPreview/DeclineSigningModal';
import DocumentPreviewProgressIndicator from '../../../components/DocumentPreviewProgressIndicator';
import DiscardLeaseChangesModal from './DocumentModal/DiscardLeaseChangesModal';
import { LEASE_ACTION_TYPES } from '../../../components/LeaseActions/Actions';
import { MOBILE_HANDOFF_TYPES } from '../../../constants/stringConstants';
import { onPrepareForLeaseSigningMobileHandoff } from '../../../utils/mobileHandoffFunctions';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';
import { logError } from '@zg-rentals/log-error';
import { emitSaveDocumentEvent } from '../../../../analytics/events';

type Props = {
  leaseId: number;
  lease: Lease;
  property?: Property;
  isColandlord?: boolean;
  onClose(): void;
  onError(notification: string): void;
  children?: React.ReactNode;
};

/**
 * Renders zdocs based leases: self upload and zillow leases based on clause management.
 */
export function DocumentModal({ leaseId, lease, property, isColandlord, onClose, onError, children }: Props) {
  const history = useHistory();
  const location = useLocation();
  const { propertyId } = useLeasingUrlParams();
  const isTenant = location.pathname.includes(RENTER_HUB_ROOT);
  const ouid = isColandlord ? Person.getOUIDfromSearch(window.location.search) : '';
  const leaseSummaryQuery = useLeaseSummaryQuery(leaseId, LeaseArtifactType.LEASE, { enabled: !isColandlord });
  const leaseSummaryFromOuidQuery = useLeaseSummaryFromOuidQuery(ouid, !!isColandlord);

  const summaryData = isColandlord ? leaseSummaryFromOuidQuery.data : leaseSummaryQuery.data;
  const documentActions = summaryData?.documentActions || [];

  const previewUrlQuery = usePreviewUrlQuery(leaseId, LeaseArtifactType.LEASE, !isColandlord);
  const previewUrlFromOuidQuery = usePreviewUrlFromOuidQuery(ouid, !!isColandlord);

  const previewData = isColandlord ? previewUrlFromOuidQuery.data : previewUrlQuery.data;
  const documentUrl = previewData?.url || '';
  const viewMode = (previewData?.mode as DocumentMode) || DocumentMode.READ;
  const hasEmbeddableEditorUrlError = isColandlord ? previewUrlFromOuidQuery.isError : previewUrlQuery.isError;
  const { user } = useRentalManagerUser();
  const [leaseLoaded, setLeaseLoaded] = useState(false);
  const [reviewComplete, setReviewComplete] = useState(false);
  const [showSigningCompleteModal, setShowSigningCompleteModal] = useRecoilState(isSigningCompleteModalOpenState);

  const markAsSignedMutation = useMarkAsSignedMutation();
  const markAsSignedFromOuidMutation = useMarkAsSignedFromOuidMutation();
  const submitDocumentChangesMutation = useSubmitDocumentChangesMutation();

  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, {
    enabled: !!propertyId,
  });
  const zrm_listing_info = webAnalyticsQuery.data;

  const isFetchingCount = useIsFetching();
  const isMutatingCount = useIsMutating();
  const isFetching = isFetchingCount > 0 || isMutatingCount > 0;
  const [showDeclineSigningModal, setShowDeclineSigningModal] = useState(false);
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [leaseEditorModified, setLeaseEditorModified] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(null);
  const [onCloseSavedClicked, setOnCloseSaveClicked] = useState(null);
  const [saveLeaseEditorRequested, setSaveLeaseEditorRequested] = useState(false);
  const { enqueueToast } = useToast();

  const queryClient = useQueryClient();

  const onErrorToast = useCallback(
    (message: string) => {
      enqueueToast(<Toast tone="critical">{message}</Toast>);
    },
    [enqueueToast],
  );

  const onInfoToast = useCallback(
    (message: string) => {
      enqueueToast(<Toast tone="info">{message}</Toast>);
    },
    [enqueueToast],
  );

  useEffect(
    function () {
      return () => {
        setShowSigningCompleteModal(false);
      };
    },
    [setShowSigningCompleteModal],
  );

  useEffect(() => {
    if (onCloseSavedClicked === true && saveInProgress === false && lease) {
      history.push(lease.getSelfServePath());
      if (lease.isSelfUpload) {
        onPrepareForLeaseSigningMobileHandoff(MOBILE_HANDOFF_TYPES.CLOSED);
      }
    }
  }, [onCloseSavedClicked, saveInProgress, lease]);

  useEffect(() => {
    if (hasEmbeddableEditorUrlError) {
      onErrorToast('Something went wrong. Try again later.');
    }
  }, [hasEmbeddableEditorUrlError]);

  useEffect(() => {
    if (saveLeaseEditorRequested) {
      const timer = setTimeout(() => {
        setSaveLeaseEditorRequested(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [saveLeaseEditorRequested]);

  function onLeaseLoad() {
    setLeaseLoaded(true);
  }

  function onLeaseClose() {
    if (leaseEditorModified) {
      setShowDiscardChangesModal(true);
    } else {
      onClose();
      if (lease?.isSelfUpload) {
        onPrepareForLeaseSigningMobileHandoff(MOBILE_HANDOFF_TYPES.CLOSED);
      }
    }
  }

  function onReviewComplete() {
    setReviewComplete(true);
  }

  function onFieldClicked() {
    if (viewMode == DocumentMode.FILL) {
      if (!reviewComplete) {
        onInfoToast('Scroll to the bottom of the document to start signing');
      }

      if (reviewComplete) {
        onInfoToast('Complete all required signatures to finish signing');
      }
    }

    if (viewMode == DocumentMode.EDIT) {
      setLeaseEditorModified(true);

      // setSaveInProgress(false);
    }
  }

  function onFieldPlaced() {
    if (viewMode == DocumentMode.EDIT) {
      setLeaseEditorModified(true);
    }
  }

  function onFieldRemoved() {
    if (viewMode == DocumentMode.EDIT) {
      setLeaseEditorModified(true);
    }
  }

  function onLeasePreviewError() {
    onError('Failed to load document preview.');
  }

  function onCloseSigningCompleteModal() {
    setShowSigningCompleteModal(false);
  }

  function onCloseDeclineSigningModal() {
    setShowDeclineSigningModal(false);
  }

  function onCancelDiscardChanges() {
    setShowDiscardChangesModal(false);
  }

  function onDiscardChanges() {
    history.push(lease.getSelfServePath());
    if (lease.isSelfUpload) {
      onPrepareForLeaseSigningMobileHandoff(MOBILE_HANDOFF_TYPES.CLOSED);
    }
  }

  function onExpiredToken() {
    console.info('Refreshing document session');

    if (isColandlord) {
      previewUrlFromOuidQuery.refetch();
    } else {
      previewUrlQuery.refetch();
    }
  }

  async function onSigningComplete() {
    console.info('Signing complete');

    try {
      if (isColandlord) {
        await markAsSignedFromOuidMutation.mutateAsync({
          getParams: {
            ouid: Person.getOUIDfromSearch(window.location.search),
          },
        });
      } else {
        await markAsSignedMutation.mutateAsync({
          postData: {
            leaseArtifactType: LeaseArtifactType.LEASE,
            leaseArtifactId: leaseId,
          },
        });
      }
    } catch (error) {
      logError({
        error,
        context: { details: 'DocumentModal - onSigningComplete', leaseId },
      });
      console.info('Failed to sign lease');
      if (isColandlord) {
        previewUrlFromOuidQuery.refetch();
      } else {
        previewUrlQuery.refetch();
      }

      return;
    }

    console.info('Lease signed');
    setShowSigningCompleteModal(true);
  }

  function onSigningError() {
    onErrorToast('Your signature was not applied, try again');
  }

  async function onEditorSaved() {
    await submitDocumentChangesMutation.mutateAsync({
      getParams: { leaseId },
    });

    await queryClient.invalidateQueries({
      predicate: (query) =>
        query.meta?.tags?.includes('leasing:lease-status-change') ||
        query.meta?.tags?.includes('leasing:lease-documents'),
    });

    setSaveInProgress(false);
    setLeaseEditorModified(false);
  }

  function onEditorSaveError() {
    onErrorToast('Your document was not saved, try again');
  }

  function onSaveClick() {
    emitSaveDocumentEvent({ lease, user, listing: zrm_listing_info });
    setSaveInProgress(true);
    setSaveLeaseEditorRequested(true);
  }

  function onSaveChangesClick() {
    setOnCloseSaveClicked(true);
    onSaveClick();
  }

  function renderModalBody() {
    return (
      <>
        {!lease?.isSelfUpload && <DocumentPreviewProgressIndicator onLoad={onLeaseLoad} />}
        <ZDocsDocumentView
          key={documentUrl}
          leaseId={leaseId}
          documentUrl={documentUrl}
          documentMode={viewMode}
          saveLeaseEditorRequested={saveLeaseEditorRequested}
          onLoad={onLeaseLoad}
          onReviewComplete={onReviewComplete}
          onFieldPlaced={onFieldPlaced}
          onFieldClicked={onFieldClicked}
          onFieldRemoved={onFieldRemoved}
          onEditorSaved={onEditorSaved}
          onEditorSaveError={onEditorSaveError}
          onSigningComplete={onSigningComplete}
          onSigningError={onSigningError}
          onError={onLeasePreviewError}
          onExpiredToken={onExpiredToken}
        />
      </>
    );
  }

  let ctaType;
  if (
    documentActions.length === 1 &&
    [LEASE_ACTION_TYPES.GO_TO_LEASES_HELP, LEASE_ACTION_TYPES.GO_TO_LEASES_TENANT_HELP].includes(
      documentActions[0].name,
    )
  ) {
    ctaType = 'link';
  } else if (documentActions[0]?.name === LEASE_ACTION_TYPES.CANCEL_SIGNING) {
    ctaType = 'secondary';
  } else {
    ctaType = null;
  }

  return (
    <>
      <Modal.Root
        open
        onOpenChange={(open) => {
          if (!open) {
            onLeaseClose();
          }
        }}
        size="fullScreen"
        data-id="lease-preview"
        useDismissProps={{ escapeKey: false, outsidePress: false }}
      >
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content>
              <Modal.Header>
                <Heading level={1} className={FULLSTORY_EXCLUDE}>
                  {property?.streetAddress ? `Lease for ${property?.streetAddress}` : 'Your lease'}
                </Heading>
              </Modal.Header>
              <Divider />
              <Modal.Body css={{ padding: '0 !important' }}>
                {renderModalBody()}
                {children}
              </Modal.Body>
              <Divider />
              <Modal.Footer>
                <LeaseActionButtonGroup
                  disabled={!leaseLoaded || isFetching || !!saveInProgress}
                  leaseActions={documentActions}
                  lease={lease}
                  property={property}
                  type={ctaType}
                  inPreview
                  onSaveClick={onSaveClick}
                  saveInProgress={saveInProgress}
                />
              </Modal.Footer>
              <Modal.CloseButton />
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
      {isTenant ? (
        <TenantSigningCompleteModal
          lease={lease}
          isOpen={showSigningCompleteModal}
          onClose={onCloseSigningCompleteModal}
        />
      ) : (
        <CountersigningCompleteModal
          lease={lease}
          isOpen={showSigningCompleteModal}
          onClose={onCloseSigningCompleteModal}
          isColandlord={isColandlord}
          property={property}
        />
      )}
      <DeclineSigningModal lease={lease} onClose={onCloseDeclineSigningModal} isOpen={showDeclineSigningModal} />
      <DiscardLeaseChangesModal
        isOpen={showDiscardChangesModal}
        onCancel={onCancelDiscardChanges}
        onDiscard={onDiscardChanges}
        onSave={onSaveChangesClick}
        disabled={!leaseLoaded || isFetching || saveInProgress}
      />
    </>
  );
}

export default DocumentModal;
